import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { confirm, haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import { parseAmountInput, formatAmount } from '../lib/format';
import type { Recurring, Currency } from '../lib/types';

interface Props { onBack: () => void; }

export default function RecurringScreen({ onBack }: Props) {
  const [items, setItems] = useState<Recurring[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  const [label, setLabel] = useState('');
  const [currency, setCurrency] = useState<Currency>('SYP');
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<'expense' | 'income'>('expense');
  const [category, setCategory] = useState<string>('');
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setErr(null);
    try {
      const [r, c] = await Promise.all([api.listRecurring(), api.listCategories()]);
      setItems(r.recurring);
      setCategories(c.categories);
      if (!category) setCategory(c.categories[0] || '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const amt = parseAmountInput(amount);
    if (!label.trim()) { setErr('Label required'); return; }
    if (amt === null) { setErr('Amount must be a positive number'); return; }
    if (dayOfMonth < 1 || dayOfMonth > 31) { setErr('Day must be 1-31'); return; }
    setErr(null); setBusy(true);
    try {
      await api.addRecurring({
        label: label.trim(),
        currency, amount: amt, kind,
        category: kind === 'expense' ? category : null,
        day_of_month: dayOfMonth,
        active: true,
      });
      setLabel(''); setAmount('');
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    } finally { setBusy(false); }
  }

  async function toggle(r: Recurring) {
    try {
      await api.updateRecurring(r.id, { active: !r.active });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }

  async function remove(r: Recurring) {
    if (!(await confirm(`Remove recurring "${r.label}"?`))) return;
    try {
      await api.removeRecurring(r.id);
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Recurring</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <form className="card" onSubmit={add}>
        <label className="label" style={{ marginTop: 0 }}>Add recurring entry</label>
        <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Rent, Salary, Netflix" maxLength={40} />

        <label className="label">Currency</label>
        <div className="segmented">
          <button type="button" className={currency === 'SYP' ? 'active' : ''} onClick={() => setCurrency('SYP')}>SYP</button>
          <button type="button" className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button>
        </div>

        <label className="label">Amount</label>
        <AmountInput value={amount} onChange={setAmount} currency={currency} />

        <label className="label">Kind</label>
        <div className="segmented">
          <button type="button" className={kind === 'expense' ? 'active' : ''} onClick={() => setKind('expense')}>Expense</button>
          <button type="button" className={kind === 'income' ? 'active' : ''} onClick={() => setKind('income')}>Income</button>
        </div>

        {kind === 'expense' && (
          <>
            <label className="label">Category</label>
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </>
        )}

        <label className="label">Day of month (1-31)</label>
        <input className="input" type="number" min={1} max={31} value={dayOfMonth} onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10) || 1)} />
        <p className="muted small" style={{ marginTop: 6, marginBottom: 0 }}>
          Fires automatically on this day each month. If a month is shorter (e.g. day 31 in Feb), fires on the last day instead.
        </p>

        <button type="submit" className="btn" disabled={busy} style={{ marginTop: 12 }}>
          {busy ? 'Saving…' : 'Add recurring'}
        </button>
      </form>

      <div className="card">
        <div className="muted small" style={{ marginBottom: 8 }}>Your recurring entries</div>
        {loading ? <div className="muted">Loading…</div> : items.length === 0 ? (
          <div className="empty">None yet.</div>
        ) : items.map((r) => (
          <div key={r.id} className="row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div>{r.label} {!r.active && <span className="muted small">(paused)</span>}</div>
              <div className="muted small">
                {r.kind === 'income' ? '+ ' : '− '}{formatAmount(Math.abs(Number(r.amount)), r.currency)}
                · day {r.day_of_month}
                {r.category ? ` · ${r.category}` : ''}
                {r.last_run_for ? ` · last ran ${r.last_run_for}` : ''}
              </div>
            </div>
            <button style={{ background: 'transparent', border: 'none', padding: '4px 8px', cursor: 'pointer', color: 'var(--link)' }} onClick={() => toggle(r)}>
              {r.active ? 'Pause' : 'Resume'}
            </button>
            <button className="btn-danger" style={{ background: 'transparent', border: 'none', padding: '4px 8px', cursor: 'pointer' }} onClick={() => remove(r)}>Remove</button>
          </div>
        ))}
      </div>
    </>
  );
}
